<template>
  <b-row v-if="!isLoading" class="justify-content-md-center m-0">
    <b-col lg="8">
      <base-bread-crumb :items="breadcrumbs" class="bg-white" />

      <learning-unit-header
        v-if="learningUnit"
        :learning-unit="learningUnit"
        :hide-status="true"
        :show-alert-review="mode === 'instructor'"
      >
      </learning-unit-header>

      <div class="font-weight-bold">Request changes</div>
      <div class="text-black-50">Change status of micro-credential</div>

      <div class="mt-5">
        <label class="mb-3" for="reviewer-feedback"> Reviewer feedback </label>

        <base-text-editor
          id="editorReviewFeedback"
          v-model="review_response"
          name="reviewer-feedback"
          class="input__feedback"
          :disabled="isSubmitting"
          :editor-options="editorOptions"
        ></base-text-editor>
      </div>

      <div class="mt-3">
        <b-button
          variant="outline-dark"
          class="mb-3"
          :disabled="isSubmitting"
          @click="openFileBrowser"
        >
          <FontAwesomeIcon icon="paperclip" class="btn__icon-paperclip mr-2" />
          Choose files</b-button
        >

        <base-s3-uploader
          id="inputAttachments"
          ref="feedbackS3Uploader"
          v-model="attachments"
          :reference-meta="{ ...referenceMeta, field: 'attachments' }"
          :disabled="isSubmitting"
          :is-public="false"
          @onFilesAdded="onFilesAdded"
          @file:removed="onRemovedFile"
        >
        </base-s3-uploader>
      </div>

      <hr class="mt-5" />

      <div class="d-flex flex-row mb-4">
        <b-button variant="success" :disabled="isSubmitting" class="mr-3" @click="clickSubmit">
          <span>Submit</span>
        </b-button>
        <b-button variant="link" :disabled="isSubmitting" @click="goBackToPreviousPage"
          >Back to previous page</b-button
        >
      </div>
    </b-col>

    <comfirm-modal
      :show.sync="activeModal"
      type="Draft"
      :learning-unit="learningUnit"
      @confirmed="submit"
    ></comfirm-modal>

    <!-- <b-modal
      v-else
      v-model="activeModal"
      centered
      :no-close-on-backdrop="true"
      :no-close-on-esc="true"
      size="md"
      title="Change status of micro-credential
    "
    >
      <div class="text-black-50">
        Are you sure you want to change the status of this micro-credential to
        draft?
      </div>

      <template #modal-footer="{ cancel }">
        <b-button variant="link" class="btn-link-dark" @click="cancel">
          Cancel
        </b-button>
        <b-button variant="success" :disabled="isSubmitting" @click="confirm">
          <span v-if="!isSubmitting">Confirm</span>

          <span v-else class="pr-2 pl-2">
            <b-spinner label="Loading..." variant="secondary" small />
          </span>
        </b-button>
      </template>
    </b-modal> -->
  </b-row>
</template>

<script>
import BaseBreadCrumb from '@/components/base-components/BaseBreadCrumb';
import { learningUnitInfoMixin } from '@/mixins/learningUnitInfoMixin';
import { userInfoMixin } from '@/mixins/userInfoMixin';
import LearningUnit from '@/models/LearningUnit';
import LearningUnitHeader from '@/components/LearningUnit/LearningUnitHeader';
import BaseTextEditor from '@/components/base-components/BaseTextEditor';
import BaseS3Uploader from '@/components/base-components/BaseS3Uploader';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import ReviewFeedback from '@/models/ReviewFeedback';
import { notificationMixin } from '@/mixins/notificationMixin';
import ComfirmModal from '@/views/LearningUnitReview/components/ComfirmModal';

export default {
  components: {
    BaseBreadCrumb,
    LearningUnitHeader,
    BaseTextEditor,
    BaseS3Uploader,
    FontAwesomeIcon,
    ComfirmModal
  },
  mixins: [userInfoMixin, learningUnitInfoMixin, notificationMixin],

  props: {
    mode: {
      type: String,
      default: 'instructor'
    },

    disabled: {
      type: Boolean,
      default: false
    }
  },

  data() {
    return {
      isSubmitting: false,
      isLoading: false,
      review_response: '',
      editorOptions: { placeholder: 'Enter your response' },
      attachments: [],
      countAttachments: 0,
      referenceMeta: {
        ref: 'review-feedback',
        refId: null
      },
      activeModal: false
    };
  },

  computed: {
    breadcrumbs() {
      const { name } = this;

      return [
        {
          key: this.mode === 'instructor' ? 'design-and-planning' : 'review-learning-unit',
          name:
            this.mode === 'instructor'
              ? 'Design and planning'
              : 'Review Submitted Micro-Credentials',
          to: {
            name: this.mode === 'instructor' ? 'design-planning' : 'review-learning-unit'
          }
        },
        {
          key: 'learning-unit-info',
          name,
          to: {
            name: 'review-learning-unit/edit',
            params: { learningUnitId: this.learningUnitId }
          }
        },
        {
          key: 'request-changes',
          name: 'Request changes',
          active: true
        }
      ];
    }
  },

  mounted() {
    this.fetch();
  },

  methods: {
    async fetch() {
      this.isLoading = true;

      LearningUnit.delete(this.learningUnitId);

      await LearningUnit.api().fetchByLearningUnitId(this.learningUnitId);

      if (
        this.mode === 'instructor' &&
        this.learningUnit &&
        !this.learningUnit.canAccess(this.user.id)
      ) {
        this.$router.replace({ name: 'learning-unit' });
      }

      this.isLoading = false;
    },

    goBackToPreviousPage() {
      this.$router.push({
        name: 'review-learning-unit/edit',
        params: { learningUnitId: this.learningUnitId }
      });
    },

    clickSubmit() {
      if (this.countAttachments || this.review_response) {
        this.activeModal = true;
      } else {
        return this.$toast.error(`At least feedback description or one attachment is required`);
      }
    },

    async submit() {
      this.isSubmitting = true;
      try {
        const { response } = await ReviewFeedback.api().create({
          review_response: this.review_response,
          learning_unit: this.learningUnit,
          reviewer: this.user,
          attachments: this.attachments
        });

        if (response && response.status === 200) {
          const { data } = response;
          await LearningUnit.api().updateLearningUnit(this.learningUnitId, {
            latest_request_changes_at: data.created_at
          });

          this.$set(this.referenceMeta, 'refId', data.id);

          if (this.$refs.feedbackS3Uploader.hasAnyQueuedFiles()) {
            await this.$refs.feedbackS3Uploader.upload();
          }

          this.$toast.success(`Request changes has been submitted`);

          this.goBackToPreviousPage();
        }
      } catch (error) {
        console.error(error);
      } finally {
        this.isSubmitting = true;
      }
    },

    openFileBrowser() {
      this.$nextTick(() => {
        this.$refs.feedbackS3Uploader.openFileBrowser();
      });
    },

    onFilesAdded(files) {
      if (files && files.length) {
        this.countAttachments += files.length;
      }
    },

    onRemovedFile(file) {
      if (file) {
        this.countAttachments--;
      }
    }
  }
};
</script>

<style lang="scss" scoped>
::v-deep #editorReviewFeedback .ql-editor {
  min-height: 20rem !important;
  word-break: break-word;

  p {
    margin-bottom: 1rem !important;
  }
}

::v-deep #inputAttachments .dz-default.dz-message {
  display: none !important;
}
</style>
